import packageJson from '../package.json';

const APP_VERSION_KEY = 'davinci.version';
const APP_VERSION: string = (packageJson as { version?: string }).version || '0.0.0';

/**
 * Okta Auth JS stores the in-flight PKCE `codeVerifier` under these prefixes (sessionStorage
 * first, localStorage fallback/legacy). A version bump must not wipe them: `/callback` is a
 * normal entry (Okta redirect and external portals), and `handleLoginRedirect()` needs the
 * verifier that was written before the user left this origin.
 *
 * Token/cache keys (`okta-token-storage`, `okta-cache-storage`) are intentionally not preserved.
 */
const PRESERVED_OKTA_OAUTH_KEY_PREFIXES = [
  'okta-transaction',
  'okta-pkce',
  'okta-shared-transaction',
  'okta-original-uri',
] as const;

export function isPreservedOktaOAuthStorageKey(key: string): boolean {
  return PRESERVED_OKTA_OAUTH_KEY_PREFIXES.some((prefix) => key.startsWith(prefix));
}

function snapshotPreservedEntries(storage: Storage): Array<[string, string]> {
  const preserved: Array<[string, string]> = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (!key || !isPreservedOktaOAuthStorageKey(key)) {
      continue;
    }
    const value = storage.getItem(key);
    if (value !== null) {
      preserved.push([key, value]);
    }
  }
  return preserved;
}

function clearStoragePreservingOktaOAuth(storage: Storage): void {
  const preserved = snapshotPreservedEntries(storage);
  storage.clear();
  for (const [key, value] of preserved) {
    storage.setItem(key, value);
  }
}

/**
 * Clears persisted app state when the deployed app version changes. Auth cookies are cleared via
 * the `clearAuthCookies` callback (see `main.ts`) so this file does not touch `document.cookie`.
 * In-flight Okta PKCE transaction keys are restored after the wipe.
 */
export function versionSync(clearAuthCookies: () => void): void {
  try {
    const storedVersion = localStorage.getItem(APP_VERSION_KEY);
    if (storedVersion !== APP_VERSION) {
      try {
        clearStoragePreservingOktaOAuth(localStorage);
      } catch (err) {
        console.warn('[VersionSync] Failed to clear localStorage', err);
      }
      try {
        clearStoragePreservingOktaOAuth(sessionStorage);
      } catch (err) {
        console.warn('[VersionSync] Failed to clear sessionStorage', err);
      }
      try {
        clearAuthCookies();
      } catch (err) {
        console.warn('[VersionSync] Failed to clear cookies', err);
      }
      try {
        localStorage.setItem(APP_VERSION_KEY, APP_VERSION);
      } catch (err) {
        console.warn('[VersionSync] Failed to set version in localStorage', err);
      }
    }
  } catch (err) {
    console.warn('[VersionSync] Version check failed; continuing bootstrap', err);
  }
}
